import { useState, useMemo, useCallback, useEffect, useRef } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Play, Clock, Calendar } from "lucide-react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import PageHeader from "../../components/PageHeader"
import { LMSBASEURL } from "../../config/apiUrls"
import Swal from 'sweetalert2';
import { isMobile } from "react-device-detect";
import { Modal } from "react-bootstrap";
import SessionVideoPlayer from "../../components/session-videoplayer";

export default function LiveSessionDashboard() {
  const { id } = useParams()
  const [currentPage, setCurrentPage] = useState(1)
  const [dateFilter, setDateFilter] = useState("all")
  const [dateRange, setDateRange] = useState("")
  const [getUpcomingSessions, setUpcomingSessions] = useState([])
  const [getTodaySessions, setTodaySessions] = useState([])
  const [getPastSessions, setPastSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("upcoming")

  const token = process.env.REACT_APP_API_TOKEN

  const fetchBatchData = useCallback(async () => {
    setLoading(true)
    const raw = JSON.stringify({
      batch_id: id,
    })

    try {
      const response = await fetch(`${LMSBASEURL}/api/get-schedule`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: raw,
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.message || "Failed to load batches")

      setUpcomingSessions(result?.upcoming_sessions ? result?.upcoming_sessions : [])
      setTodaySessions(result?.today_sessions ? result?.today_sessions : [])
      setPastSessions(result?.past_sessions ? result?.past_sessions : [])
    } catch (error) {
      console.error("Error fetching batch data:", error)
      setUpcomingSessions([])
      setTodaySessions([])
      setPastSessions([])
    } finally {
      setLoading(false)
    }
  }, [id, token])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("video") === "true") {
      setActiveTab("past")
    }
    fetchBatchData()
  }, [fetchBatchData])


  const itemsPerPage = 5

  const handleDateFilterChange = (val) => {
    setDateFilter(val)
    if (val !== "all") {
      setDateRange("")
    }
    setCurrentPage(1)
  }

  const filteredSessions = useMemo(() => {
    const targetSessions = activeTab === "upcoming" ? getUpcomingSessions : getPastSessions
    return targetSessions.filter((session) => {
      let matchesDate = true
      if (dateFilter !== "all") {
        const today = new Date()
        const sessionDate = new Date(session.date)
        switch (dateFilter) {
          case "today":
            matchesDate = sessionDate.toDateString() === today.toDateString()
            break
          case "week":
            const weekStart = new Date(today)
            weekStart.setDate(today.getDate() - today.getDay())
            const weekEnd = new Date(weekStart)
            weekEnd.setDate(weekStart.getDate() + 6)
            matchesDate = sessionDate >= weekStart && sessionDate <= weekEnd
            break
          case "month":
            matchesDate =
              sessionDate.getMonth() === today.getMonth() && sessionDate.getFullYear() === today.getFullYear()
            break
        }
      }
      const matchesDateRange = dateRange === "" || session.date === dateRange
      return matchesDate && matchesDateRange
    })
  }, [getUpcomingSessions, getPastSessions, activeTab, dateFilter, dateRange])

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage)
  const currentSessions = filteredSessions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const handleStartSession = async (session) => {
    if (session?.meet_url) {
      // Call attendance API
      try {
        const studentId = sessionStorage.getItem("id");
        const now = new Date();
        // Format date as YYYY-MM-DD HH:mm:ss
        const joinTime = now.getFullYear() + '-' +
          String(now.getMonth() + 1).padStart(2, '0') + '-' +
          String(now.getDate()).padStart(2, '0') + ' ' +
          String(now.getHours()).padStart(2, '0') + ':' +
          String(now.getMinutes()).padStart(2, '0') + ':' +
          String(now.getSeconds()).padStart(2, '0');

        const raw = JSON.stringify({
          batch_schedule_id: session.id,
          batch_id: session.batch_id,
          course_id: session.course_id,
          student_id: studentId,
          attendance_status: "present",
          join_time: joinTime,
          remark: ""
        });

        // We don't await this or we catch error so it doesn't block joining
        fetch(`${LMSBASEURL}/api/batch-live-session/attendance`, {
          method: 'POST',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: raw
        }).then(response => {
          if (!response.ok) console.warn("Attendance API failed");
        }).catch(err => console.error("Attendance error:", err));

      } catch (error) {
        console.error("Error recording attendance:", error);
      }

      window.open(session?.meet_url, "_blank");
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'No Google Meet URL found. Please contact your teacher.',
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'OK'
      });
    }
  }

  if (loading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  const getDateFilter = (date) => {
    setDateFilter('all')
    setDateRange(date)
  }

  return (
    <>
      <PageHeader
        title={`All Sessions`}
        breadcrumbs={[
          { label: "Dashboard", path: "/dashboard" },
          { label: "Batches", active: true },
        ]}
      />

      {getTodaySessions.length > 0 && (
        <TodaysSessions sessions={getTodaySessions} maxItems={3} onStartSession={handleStartSession} />
      )}

      <SessionFilters
        dateFilter={dateFilter}
        dateRange={dateRange}
        onDateFilterChange={handleDateFilterChange}
        onDateRangeChange={getDateFilter}
      />

      <SessionsTable
        key={activeTab}
        sessions={currentSessions}
        totalPages={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        tab={activeTab}
        setActiveTab={setActiveTab}
        activeTab={activeTab}
        setCurrentPage={setCurrentPage}
        setDateFilter={setDateFilter}
        setDateRange={setDateRange}
        onStartSession={handleStartSession}
      />
    </>
  )
}

function SessionFilters({ dateFilter, dateRange, onDateFilterChange, onDateRangeChange }) {
  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label small text-muted">Filter by Date</label>
            <select className="form-select" value={dateFilter} onChange={(e) => onDateFilterChange(e.target.value)}>
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small text-muted">Specific Date</label>
            <input
              type={dateRange ? "date" : "text"}
              className="form-control"
              placeholder="Select date"
              value={dateRange}
              onClick={(e) => {
                if (e.target.type !== "date") {
                  e.target.type = "date";
                  e.target.showPicker?.()
                }
              }}
              onBlur={(e) => !e.target.value && (e.target.type = "text")}
              onChange={(e) => onDateRangeChange(e.target.value)}
            />
          </div>
        </div>
      </div>
      <style jsx>
        {`input::placeholder {
          opacity: 0.5;
          color: black !important;
        }`}
      </style>
    </div>
  )
}

function SessionsTable({ sessions, totalPages, currentPage, onPageChange, setActiveTab, activeTab, tab, onStartSession, setCurrentPage, setDateFilter, setDateRange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [VideoUrl, setVideoUrl] = useState("")
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [viewed, setViewed] = useState(false);
  const [showFallback, setShowFallback] = useState(false)



  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const getSessionStatus = (session) => {
    if (session.postpone_status === 1) return "postponed"

    const now = new Date()
    const sessionStart = new Date(`${session.date}T${session.start_time}`)
    const sessionEnd = new Date(`${session.date}T${session.end_time}`)

    if (now < sessionStart) return "upcoming"
    if (now >= sessionStart && now <= sessionEnd) return "live"
    return "completed"
  }


  const handleOpenVideoPopUp = (url) => {
    if (!url) {
      Swal.fire({
        title: 'Video Url not Found.',
        text: 'Please contact your teacher.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
    } else {
      setVideoUrl(url)
      setShowVideoModal(true)
    }
  }

  const handleCancel = () => {
    setShowVideoModal(false);
  };

  const handleVideoEnded = () => {
    if (!viewed) {
      console.log('Video ended, could auto-mark as viewed');
    }
    setIsPlaying(false); // Update play state
  };

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
  };

  const size = isMobile ? 58 : 77;
  const iconSize = isMobile ? 30 : 55;

  const handleVideoError = (e) => {
    const video = e.currentTarget;
    console.warn("Video error:", e);

    // Retry logic once or twice
    if (!video.dataset.retried) {
      video.dataset.retried = true;
      const currentTime = video.currentTime;
      setTimeout(() => {
        video.load();
        video.currentTime = currentTime;
        video.play().catch(console.warn);
      }, 1500);
    } else {
      setShowFallback(true);
    }
  };


  return (
    <>
      <div className="card border">
        <div className="p-2">
          <div className="d-flex justify-content-end gap-3">
            <button
              className={`btn ${activeTab === "upcoming" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setActiveTab("upcoming")
                setCurrentPage(1)
                setDateFilter("all")
                setDateRange("")

                const params = new URLSearchParams(location.search);
                params.delete("video");

                const newSearch = params.toString();
                const newPath = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

                navigate(newPath, { replace: true });
              }}
            >
              Upcoming Sessions
            </button>
            <button
              className={`btn ${activeTab === "past" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setActiveTab("past")
                setCurrentPage(1)
                setDateFilter("all")
                setDateRange("")
              }}
            >
              Past Sessions
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {sessions.length === 0 ? (
            <div className="py-5 text-center">
              <Calendar className="mb-3 text-muted" size={48} />
              <h5 className="text-muted">No sessions found</h5>
              <p className="text-muted">No sessions match your current filter criteria</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="py-3 px-4 fw-semibold">Date</th>
                      <th className="py-3 px-4 fw-semibold">Day</th>
                      <th className="py-3 px-4 fw-semibold">Time</th>
                      <th className="py-3 px-4 fw-semibold">Status</th>
                      <th className="py-3 px-4 fw-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => {
                      const status = getSessionStatus(session)
                      return (
                        <tr key={session.id}>
                          <td className="py-3 px-4">
                            <div className="d-flex align-items-center">
                              <Calendar size={16} className="text-muted me-2" />
                              {formatDate(session.date)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-capitalize fw-medium">{session.day}</td>
                          <td className="py-3 px-4">
                            <div className="d-flex align-items-center">
                              <Clock size={16} className="text-muted me-2" />
                              {formatTime(session.start_time)} - {formatTime(session.end_time)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {status === "postponed" && <span className="badge bg-danger">Postponed</span>}
                            {status === "upcoming" && <span className="badge bg-primary">Upcoming</span>}
                            {status === "live" && <span className="badge bg-success">Live</span>}
                            {status === "completed" && <span className="badge bg-secondary">Completed</span>}
                          </td>
                          <td className="py-3 px-4">
                            {tab == "upcoming" ?
                              <>
                                {status === "upcoming" && (
                                  <button className="btn btn-sm btn-outline-success d-flex align-items-center" disabled>
                                    <Play size={14} className="me-1" />
                                    Upcoming
                                  </button>
                                )}
                                {status === "live" && (
                                  <button className="btn btn-sm btn-danger d-flex align-items-center" onClick={() => onStartSession(session)}>
                                    <Play size={14} className="me-1" />
                                    Join Now
                                  </button>
                                )}
                                {status === "completed" && (
                                  <button className="btn btn-sm btn-outline-secondary" disabled>
                                    Completed
                                  </button>
                                )}
                                {status === "postponed" && (
                                  <button className="btn btn-sm btn-outline-danger" disabled>
                                    Postponed
                                  </button>
                                )}
                              </>
                              :
                              <button onClick={() => handleOpenVideoPopUp(session?.recording_file)} className="btn btn-sm btn-danger d-flex align-items-center">
                                <Play size={14} className="me-1" />
                                Play Video
                              </button>
                            }
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mobile-table border-top">
                {sessions.map((session) => {
                  const status = getSessionStatus(session);
                  return (
                    <div key={session.id} className="border-bottom">
                      <div className="card-body">
                        <p><strong>Date:</strong> {formatDate(session.date)}</p>
                        <p><strong>Day:</strong> {session.day}</p>
                        <p><strong>Time:</strong> {formatTime(session.start_time)} - {formatTime(session.end_time)}</p>
                        <p>
                          <strong>Status:</strong>{" "}
                          {status === "upcoming" && <span className="badge bg-primary">Upcoming</span>}
                          {status === "live" && <span className="badge bg-success">Live</span>}
                          {status === "completed" && <span className="badge bg-secondary">Completed</span>}
                          {status === "postponed" && <span className="badge bg-danger">Postponed</span>}
                        </p>
                        <div>
                          {tab == "upcoming" ? <>
                            {status === "upcoming" && (
                              <button className="btn btn-sm btn-outline-success w-100" disabled>
                                <Play size={14} className="me-1" />
                                Upcoming
                              </button>
                            )}
                            {status === "live" && (
                              <button className="btn btn-sm btn-danger w-100" onClick={() => onStartSession(session)}>
                                Join Now
                              </button>
                            )}
                            {status === "completed" && (
                              <button className="btn btn-sm btn-outline-secondary w-100" disabled>
                                Completed
                              </button>
                            )}
                            {status === "postponed" && (
                              <button className="btn btn-sm btn-outline-danger w-100" disabled>
                                Postponed
                              </button>
                            )}
                          </> :

                            <button onClick={() => handleOpenVideoPopUp(session?.recording_file)} className="btn btn-sm btn-outline-danger w-100">
                              <Play size={14} className="me-1" />
                              Play Video
                            </button>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-end mt-4">
          <nav>
            <ul className="pagination">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index} className={`page-item ${currentPage === index + 1 ? "active" : ""}`}>
                  <button className="page-link" onClick={() => onPageChange(index + 1)}>
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      <Modal
        show={showVideoModal}
        onHide={handleCancel}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
        enforceFocus={false}
        restoreFocus={false}
      >
        <Modal.Header closeButton className="p-3">
          <Modal.Title className="fw-bold text-primary">
            Past Recording Sessions
          </Modal.Title>
        </Modal.Header>
        {/* <Modal.Body className="p-0" style={{ minHeight: "400px",overflow: "hidden"  }}>
          <div onClick={(e) => e.stopPropagation()}>
            <SessionVideoPlayer
              src={"https://d19yci31ipq89u.cloudfront.net/merged-videos/2025-11-27/14/67c8ccec-302b-4a82-a817-540dcf87fdf8-14_771_285.mp4"}
            />
          </div>
        </Modal.Body> */}
        <Modal.Body className="p-0">
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              height: "400px",
              width: "100%",
              background: "black",
              overflow: "hidden",
            }}
          >
            <video
              src={VideoUrl}
              controls
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              preload="metadata"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          </div>
        </Modal.Body>
      </Modal>
      <style>{`
      @media (max-width: 768px) {
        .table {
          display: none;
        }
        .mobile-table {
          display: block;
        }
      }
      @media (min-width: 769px) {
        .mobile-table {
          display: none;
        }
      }
    `}</style>
    </>
  )
}

export function TodaysSessions({ sessions, onStartSession, maxItems = 3, from = "auto" }) {
  const [searchParams] = useSearchParams();
  const [showMoreButton, setShowMoreButton] = useState(sessions?.length < 3 ? false : true);
  const [maxItemstate, setMaxItems] = useState(maxItems || 3);
  const givemeLink = searchParams.has('givemelink');
  const Student_ID = searchParams.get('id');

  if (!sessions || sessions.length === 0) {
    return null
  }

  const displaySessions = maxItemstate ? sessions.slice(0, maxItemstate) : sessions

  const handleMore = () => {
    const newMaxItems = maxItemstate + 3;
    setMaxItems(newMaxItems);

    if (newMaxItems >= sessions.length) {
      setShowMoreButton(false);
    }
  };

  const getSessionStatus = (session) => {
    if (session.postpone_status === 1) return "postponed"

    const now = new Date()
    const sessionStart = new Date(`${session.date}T${session.start_time}`)
    const sessionEnd = new Date(`${session.date}T${session.end_time}`)
    const earlyLiveStart = new Date(sessionStart.getTime() - 10 * 60 * 1000)

    if (now < earlyLiveStart) return "upcoming"
    if (now >= earlyLiveStart && now <= sessionEnd) return "live"
    return "completed"
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "live":
        return <span className="badge bg-danger">Live</span>
      case "upcoming":
        return <span className="badge bg-primary">Upcoming</span>
      case "completed":
        return <span className="badge bg-secondary">Completed</span>
      case "postponed":
        return <span className="badge bg-warning">Postponed</span>
      default:
        return null
    }
  }

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const cssStyle = { borderTopRightRadius: 12, borderTopLeftRadius: 12 }

  return (
    <div className="mb-4">
      <div className={from === "dashboard" ? "card yellowborder" : "card"}>
        {from === "dashboard" ? (
          <div className="card-header align-items-center d-flex position-relative">
            <h4 className="card-title1 mb-0 flex-grow-1">Today's Sessions</h4>
            <div className="flex-shrink-0">
              <Link to="/batches">
                <button type="button" className="btn btn-soft-primary btn-sm">
                  View Batch <i className="ri-arrow-right-line align-bottom"></i>
                </button>
              </Link>
            </div>
          </div>) :
          <div
            className={from === "dashboard" ? "card-header bg-white fw-bold d-flex align-items-center" : "card-header bg-white fw-bold d-flex align-items-center border-bottom"}
            style={from === "dashboard" ? cssStyle : {}}
          >
            <Calendar className="me-2" size={18} />
            Today's Sessions
          </div>}
        <div className="card-body">
          {displaySessions.map((session) => {
            const status = getSessionStatus(session)
            return (
              <div key={session.id} className="row align-items-center mb-3 p-3 border rounded bg-light">
                <div className="col-md-9">
                  <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                    {getStatusBadge(status)}
                    {/* <span className="fw-medium">Course Name: {session.course_title}</span> */}
                  </div>
                  <div className="d-flex flex-wrap gap-3 text-muted small">
                    <div className="d-flex align-items-center">
                      <Clock size={14} className="me-1" />
                      <span>
                        {formatTime(session.start_time)} - {formatTime(session.end_time)}
                      </span>
                    </div>
                    <span className="text-capitalize">{session.day}</span>
                  </div>
                </div>
                <div className="col-md-3 text-end d-flex">
                  {givemeLink && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(session.meet_url);
                        alert("Meeting link copied to clipboard!");
                      }}
                      className="btn btn-sm btn-success d-flex align-items-center ms-auto"
                    >
                      <i className="ri-file-copy-line me-1"></i>
                      Copy Link
                    </button>
                  )}
                  {status === "upcoming" && (
                    <button
                      onClick={() => onStartSession?.(session)}
                      className="btn btn-sm btn-success d-flex align-items-center ms-auto"
                      disabled
                    >
                      <Play size={14} className="me-1" />
                      Up Coming
                    </button>
                  )}
                  {status === "live" && (
                    <button
                      onClick={() => onStartSession?.(session)}
                      className="btn btn-sm btn-danger d-flex align-items-center ms-auto"
                    >
                      <Play size={14} className="me-1" />
                      Join Now
                    </button>
                  )}
                  {status === "completed" && (
                    <button className="btn btn-sm btn-outline-secondary ms-auto" disabled>
                      Completed
                    </button>
                  )}
                  {status === "postponed" && (
                    <button className="btn btn-sm btn-outline-warning ms-auto" disabled>
                      Postponed
                    </button>
                  )}
                </div>
              </div>
            )
          })}
          {showMoreButton && (
            <div className="d-flex justify-content-center">
              <button onClick={handleMore} type="button" className="btn btn-soft-primary btn-sm">
                View More <i className="ri-arrow-down align-bottom"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

